/* eslint-disable prettier/prettier */
import React, { useState, useContext, useRef, useCallback, Suspense, useEffect } from "react";
import {
	AppBar,
	CssBaseline,
	Divider,
	Drawer,
	Hidden,
	Modal,
	IconButton,
	List,
	ListItem,
	ListItemText,
	ListItemAvatar,
	Toolbar,
	Avatar,
	Badge,
	Menu,
	MenuItem,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	makeStyles,
	useTheme,
	withStyles,
	Popover,
	Chip,
	Tooltip
} from "@material-ui/core";
import { Outlet, useNavigate } from "react-router-dom";
import { DialogProps, PdfViewerDialogProps } from "../../../../@types/dialogProps.types";
import { api } from "../../../../utils/api";
import { io, Socket } from "socket.io-client";
import { ADMIN_ENV, port, url } from "../../../../config/config";
import { NOTIFICATION_TYPE } from "../../../../constants/notification/notificationType";
import { AuthContext, UIContext } from "../../../../contexts";
import { loader } from "../../../../utils/commonFunctions/loader";
import { CustomDialog } from "../../../shared";
import { LazyNotifications, LazyCustomDialog } from "../../../shared";
import SIDEBAR_OPTIONS from "../../../../constants/sidebarOptions";
import { Tab } from "../../../../@types/sidebarOptions.types";
import { SidebarProps } from "../../../../@types/components/sidebar.types";
import { PlanBrochurePdf, nexcaliberLogo } from "../../../../assets";
import { dashboardHelpPDF } from "../../../../assets";

import MailIcon from "@material-ui/icons/Mail";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import MenuIcon from "@material-ui/icons/Menu";
import AccountCircle from "@material-ui/icons/AccountCircle";
import NotificationsIcon from "@material-ui/icons/Notifications";
import LiveHelpIcon from "@material-ui/icons/LiveHelp";
import ContactSupportIcon from "@material-ui/icons/ContactSupport";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
// import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import MoreIcon from "@material-ui/icons/MoreVert";
import PersonIcon from "@material-ui/icons/Person";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";

import "./sidebar.css";
import { FormControl, Select } from "@material-ui/core";
import DropDown from "../../../shared/dropDown/DropDown";
import initCapitalize from "../../../../utils/commonFunctions/initCapitalize";
import ROLES from "../../../../constants/roles";
import { trackPromise } from "react-promise-tracker";
import { LazyCustomPdfViewerDialog } from "../..";

import { notificationSound } from "../../../../assets";
import { useAudio } from "../../../../hooks/useAudio";
import { permissions } from "../../../../permissions";
import dynamicSidebarOptions from "../../../shared/dynamicSideBarOptions/DynamicSidebarOptions";

const drawerWidth = 240;

// eslint-disable-next-line arrow-parens
const useStyles = makeStyles((theme) => ({
	application: {
		display: "flex"
	},
	drawer: {},
	appBar: {},
	menuButton: {
		marginRight: theme.spacing(2),
		[theme.breakpoints.up("sm")]: {
			// display: "",
			backgroundColor: "#EEEEEE",
			color: "black"
		}
	},
	// necessary for content to be below app bar
	toolbar: theme.mixins.toolbar,
	drawerPaper: {},
	content: {
		flexGrow: 1,
		minHeight: "100vh",
		padding: theme.spacing(3)
	},
	avatarColor: {
		color: "black",
		padding: 40
	},
	sectionDesktop: {
		display: "none",
		[theme.breakpoints.up("md")]: {
			display: "flex"
		}
	},
	sectionMobile: {
		display: "flex",
		[theme.breakpoints.up("md")]: {
			display: "none"
		}
	},
	grow: {
		flexGrow: 1
	}
}));

// eslint-disable-next-line arrow-parens
const StyledBadge = withStyles((theme) => ({
	badge: {
		backgroundColor: "#44b700",
		color: "#44b700",
		boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
		"&::after": {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			borderRadius: "50%",
			animation: "$ripple 1.2s infinite ease-in-out",
			border: "1px solid currentColor",
			// eslint-disable-next-line quotes
			content: '""'
		}
	},
	"@keyframes ripple": {
		"0%": {
			transform: "scale(.8)",
			opacity: 1
		},
		"100%": {
			transform: "scale(2.4)",
			opacity: 0
		}
	}
}))(Badge);

const Sidebar = (): JSX.Element => {
	const notificationAudio = useAudio(notificationSound);
	const classes = useStyles();
	const theme = useTheme();
	const { user, setUser } = useContext(AuthContext);
	const [drawerWidth, setDrawerWidth] = useState(240);
	const [groupName, setGroupName] = useState([
		{ label: "Brookshire Brothers, Inc.", value: "brookshire_brothers_inc" },
		{ label: "Aeonix Research & Innovations", value: "aeonix_research_innovations" },
		{ label: "Dalas Wholesale", value: "dalas_wholesale" },
		{ label: "Nexcaliber, Inc.", value: "nexcaliber_inc" },
		{ label: "Brookshire Brothers 3, Inc.", value: "brookshire_brothers_3_inc" }
	]);
	const [selectedGroupName, setSelectedGroupName] = useState("brookshire_brothers_inc");
	const [mobileOpen, setMobileOpen] = useState<boolean>(true);
	const [openPdfViewDialogProps, setOpenPdfViewDialogProps] = useState<PdfViewerDialogProps>({
		openDialog: false
	});
	const [hasFetchedAllNotifications, setHasFetchedAllNotifications] = useState<boolean>(false);
	const [limit, setLimit] = useState<number>();
	const [isAccordionSelected, setIsAccordionSelected] = useState<boolean>(true);
	const [anchorEl, setAnchorEl] = useState<(EventTarget & (HTMLLIElement | HTMLButtonElement)) | null>(null);
	const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState<(EventTarget & HTMLButtonElement) | null>(null);
	const [popOverAnchorEl, setPopOverAnchorEl] = useState<(EventTarget & SVGSVGElement) | null>(null);
	const [accordionSelectedIndex, setAccordionSelectedIndex] = useState(null);
	const { dashboardHeader, selectedTab, setDashboardHeader } = useContext(UIContext);
	const [oldNotifications, setOldNotifications] = useState<any>([]);
	const [newNotifications, setNewNotifications] = useState<any>([]);
	const [notificationCount, setNotificationCount] = useState<number | null>(null);
	const [hasFetchedInitialNotifications, setHasFetchedInitialNotifications] = useState<boolean>(false);

	const [sideBarOptions, setSideBarOptions] = useState<any>([]);

	// notification sound alert
	const [logoutDialogProps, setLogoutDialogProps] = useState<DialogProps>({
		openDialog: false,
		title: "",
		content: "",
		actions: []
	});
	const navigate = useNavigate();
	const isMenuOpen = Boolean(anchorEl);
	const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
	const isPopOverOpen = Boolean(popOverAnchorEl);
	const id = isPopOverOpen ? "simple-popover" : undefined;
	const [pageNumber, setPageNumber] = useState<number>(0);
	const [skeletonLoad, setSkeletonLoad] = useState<boolean>(false);
	const loading = useRef<boolean>(false);

	const scrollToEnd = useCallback(() => {
		if (loading.current === false) {
			setPageNumber(pageNumber + 1);
		}
	}, [pageNumber]);

	const scrollEvent = useCallback(
		(event: React.UIEvent<HTMLDivElement>) => {
			const { scrollHeight, scrollTop, clientHeight } = event.target as HTMLDivElement;

			if (scrollTop + clientHeight > scrollHeight - 5) {
				scrollToEnd();
			}
		},
		[scrollToEnd]
	);

	const getNotifications = useCallback(async () => {
		const params = {
			page: pageNumber + 1,
			limit
		};
		const response = await api.notification.getNotificationsByUserId(params);
		if (response === false) {
			setHasFetchedAllNotifications(true);
			setSkeletonLoad(false);
			loading.current = false;
		}
		if (response) {
			setNewNotifications(Object.assign([], [...response.newNotifications]));
			setOldNotifications((prevOldNotification: any[]) => {
				return Object.assign([], [...prevOldNotification, ...response.oldNotifications]);
			});
			loading.current = false;
			setSkeletonLoad(false);
			setHasFetchedInitialNotifications(true);
		}
	}, [limit, pageNumber]);

	const handlePopOverOpen = (event: React.MouseEvent<SVGSVGElement>) => {
		setPopOverAnchorEl(event.currentTarget);
		setNotificationCount(null);
		const notificationArrowElement = document.getElementById("notificationArrow");
		if (notificationArrowElement) {
			notificationArrowElement.style.display = "block";
		}
	};

	// const handleGroupNameChange = useCallback((event: any) => {
	// 	setSelectedGroupName(event.target.value);
	// }, []);

	const handlePopOverClose = useCallback(async () => {
		setPopOverAnchorEl(null);
		const notificationArrowElement = document.getElementById("notificationArrow");

		if (notificationArrowElement) {
			notificationArrowElement.style.display = "none";
		}

		const response = await api.notification.setAllNotificationsAsOldByUserId();
		if (response) {
			setOldNotifications(Object.assign([], [...response.oldNotifications]));
			setNewNotifications(Object.assign([], response.newNotifications));
		}
	}, []);

	const listenNotification = useCallback(() => {
		if (!user) {
			return;
		}
		const socket = io(`${url}:${port}`);
		// console.log("Before user return");

		socket.on(NOTIFICATION_TYPE.super_admin_sign_up, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any[]) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.registration, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.creation, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.edit, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.approval, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.member_chat_support, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.employer_chat_support, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.blogs, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.announcements, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.statement_of_understanding, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.registration_help, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.plan_creation, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.open_enrollment, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.enrollment_submission, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.terminate, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.reinstate, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});

		socket.on(NOTIFICATION_TYPE.rehire, (message: any) => {
			if (message.assingedUserIds.includes(user._id)) {
				setNewNotifications((prevNewNotifications: any) => {
					return [message, ...prevNewNotifications];
				});
			}
		});
	}, [user]);

	const getUser = useCallback(async () => {
		try {
			const response = await trackPromise(api.groupOwner.getGroupOwnerByAuth());
			// console.log("Users", response);
			let _SSN;

			const { SSN } = response;
			// Converting SSN to required type
			const ssn = String(SSN);
			if (ssn) _SSN = ssn.substring(0, 3) + "-" + ssn.substring(3, 5) + "-" + ssn.substring(5);
			if (response) {
				const _user = {
					...response,
					SSN: SSN !== "" || SSN !== undefined ? _SSN : ""
				};
				setUser(Object.assign({}, _user));
			}
		} catch (err) {
			alert("Failed to fetch User!");
		}
	}, [setUser]);

	const handlePdfViewDialogOpen = useCallback(() => {
		setOpenPdfViewDialogProps(Object.assign({}, openPdfViewDialogProps, { openDialog: true }));
	}, [openPdfViewDialogProps]);

	const handlePdfViewDialogClose = useCallback(() => {
		setOpenPdfViewDialogProps(Object.assign({}, openPdfViewDialogProps, { openDialog: false }));
	}, [openPdfViewDialogProps]);

	const handleProfileMenuOpen = (event: React.MouseEvent<HTMLLIElement | HTMLButtonElement>) => {
		const { currentTarget } = event;
		setAnchorEl(currentTarget);
		const menuArrowElement = document.getElementById("menuArrow");
		if (menuArrowElement) {
			menuArrowElement.style.display = "block";
		}
	};

	const handleMobileMenuClose = () => setMobileMoreAnchorEl(null);

	const handleLogoutClick = useCallback(() => {
		setLogoutDialogProps(
			Object.assign({}, logoutDialogProps, {
				openDialog: true,
				title: "Thank you",
				content: "Do You Want to log out of Nexcaliber Portal",
				actions: [
					{
						label: "Cancel",
						callback: () => {
							// window.location.reload();
							// navigate("/login");
							setLogoutDialogProps(Object.assign({}, logoutDialogProps, { openDialog: false }));
						}
					},
					{
						label: "Okay",
						callback: () => {
							// window.location.reload();
							localStorage.removeItem("@jwt");
							navigate("/login");
							setLogoutDialogProps(Object.assign({}, logoutDialogProps, { openDialog: false }));
						}
					}
				]
			})
		);
	}, [logoutDialogProps, navigate]);

	const handleMenuClose = () => {
		setAnchorEl(null);
		const menuArrowElement = document.getElementById("menuArrow");
		if (menuArrowElement) {
			menuArrowElement.style.display = "none";
		}
		handleMobileMenuClose();
	};

	const handleMobileMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		const { currentTarget } = event;
		setMobileMoreAnchorEl(currentTarget);
	};

	const handleDrawerToggle = useCallback(() => {
		setMobileOpen(!mobileOpen);
		if (mobileOpen === true) {
			setDrawerWidth(0);
		} else {
			setDrawerWidth(240);
		}
	}, [mobileOpen]);

	const handleProfileClick = useCallback(() => {
		navigate("/my-profile");
		setAnchorEl(null);
		handleMobileMenuClose();
	}, [navigate]);

	const handleNameClick = useCallback(() => {
		navigate("/my-profile");
	}, [navigate]);

	const renderNotifications = (
		<div className="admin-notification-container">
			{/* <PopoverArrow/> */}

			<Popover
				className="notification-popover"
				id={id}
				open={isPopOverOpen}
				anchorEl={popOverAnchorEl}
				onClose={handlePopOverClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center"
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center"
				}}
			>
				{/* <PopoverArrow /> */}
				<Suspense>
					<LazyNotifications
						user={user}
						newNotifications={newNotifications}
						oldNotifications={oldNotifications}
						scrollEvent={scrollEvent}
						loading={loading}
						skeletonLoad={skeletonLoad}
					/>
				</Suspense>
			</Popover>
		</div>
	);

	const menuId = "primary-search-account-menu";

	const renderMenu = (
		<Popover
			id={menuId}
			open={isMenuOpen}
			anchorEl={anchorEl}
			onClose={handleMenuClose}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "center"
			}}
			transformOrigin={{
				vertical: "top",
				horizontal: "center"
			}}
			style={
				{
					//scrollbar: 5
				}
			}
		>
			<div className="profile-menu">
				<List>
					<ListItem button onClick={handleProfileClick}>
						<ListItemAvatar>
							<Avatar className="list-avatar">
								<PersonIcon /* style={{ color: "#44b700" }} */ />
							</Avatar>
						</ListItemAvatar>
						<ListItemText primary="My Profile" />
					</ListItem>
					<ListItem button onClick={handleLogoutClick}>
						<ListItemAvatar>
							<Avatar className="list-avatar">
								<ExitToAppIcon /* style={{ color: "#44b700"}} */ />
							</Avatar>
						</ListItemAvatar>
						<ListItemText primary="Logout" />
					</ListItem>
				</List>
			</div>
		</Popover>
	);

	const mobileMenuId = "primary-search-account-menu-mobile";
	const renderMobileMenu = (
		<Menu
			anchorEl={mobileMoreAnchorEl}
			anchorOrigin={{ vertical: "top", horizontal: "right" }}
			id={mobileMenuId}
			keepMounted
			transformOrigin={{ vertical: "top", horizontal: "right" }}
			open={isMobileMenuOpen}
			onClose={handleMobileMenuClose}
		>
			<div className="sec-desk">
				{/* <MenuItem>
					<IconButton aria-label="show 4 new mails">
						<Badge badgeContent={4} color="secondary">
							<MailIcon style={{ color: "#4e4e4e" }} />
						</Badge>
					</IconButton>
					<p>Messages</p>
				</MenuItem> */}
				<a href="/all-notifications">
					<MenuItem>
						<IconButton aria-label="show 11 new notifications">
							<Badge badgeContent={11} color="secondary">
								<NotificationsIcon style={{ color: "#4e4e4e" }} />
							</Badge>
						</IconButton>
						<p>Notifications</p>
					</MenuItem>
				</a>
				<MenuItem onClick={handleProfileMenuOpen}>
					<IconButton
						aria-label="account of current user"
						aria-controls="primary-search-account-menu"
						aria-haspopup="true"
						color="inherit"
					>
						<AccountCircle style={{ color: "#4e4e4e" }} />
					</IconButton>
					<p>Profile</p>
				</MenuItem>
			</div>
		</Menu>
	);

	// const drawer = (

	// );

	useEffect(() => {
		const muteStatusInLS = window.localStorage.getItem("MUTE_STATUS");
		if (muteStatusInLS === null || muteStatusInLS === undefined)
			window.localStorage.setItem("MUTE_STATUS", JSON.stringify(false));
	}, []);

	useEffect(() => {
		if (
			newNotifications.length > 0 &&
			hasFetchedInitialNotifications === true &&
			window.localStorage.getItem("MUTE_STATUS") === "false"
		) {
			notificationAudio.play();
		}
		setNotificationCount(newNotifications.length);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasFetchedInitialNotifications, newNotifications.length]);

	// useEffect(() => {
	// 	if (newNotifications && notificationTrigger === true) {
	// 		notificationAudio.play();
	// 		setNotificationTrigger(false);
	// 	}
	// }, [newNotifications, notificationAudio, notificationTrigger]);

	useEffect(() => {
		listenNotification();
	}, [listenNotification]);

	useEffect(() => {
		setLimit(8);
		getUser();
		// console.log("ue2");
	}, [getUser]);

	useEffect(() => {
		// console.log("ue3");
		if (pageNumber !== 0 && !hasFetchedAllNotifications) {
			loading.current = true;
			setSkeletonLoad(true);
		}
		if (limit && !hasFetchedAllNotifications) {
			getNotifications();
		}
	}, [getNotifications, hasFetchedAllNotifications, limit, pageNumber]);

	useEffect(() => {
		if (user) {
			const permission = (user as any).permissions.filter((permission: any) => permission.group_number === 0);
			// console.log(permission, "permission", user);
			if (permission && permission.length > 0) {
				const sideBarOptions = dynamicSidebarOptions(
					user.role,
					permission[0].permission_and_subscription.permissions
				);
				setSideBarOptions(sideBarOptions);
			} else {
				// alert("You don't have permission to access this");
			}
		}
	}, [user]);

	// console.log(sideBarOptions, "sideBarOptions");

	// console.log("setHasFetchedInitialNotifications => ", hasFetchedInitialNotifications);
	// console.log("mute status => ", muteStatus);

	const container = window !== undefined ? () => window.document.body : undefined;

	return (
		<div className={classes.application + " admin-sidebar"}>
			{/* <div
				id="notificationArrow"
				className={
					window.innerWidth - document.body.clientWidth === 0
						? "admin-notificationArrowNoScroll"
						: "admin-notificationArrowWithScroll"
				}
				style={{
					display: "none"
				}}
			></div>
			<div
				id="menuArrow"
				style={{
					width: "0",
					height: "0",
					borderLeft: "7px solid transparent",
					borderRight: "7px solid transparent",
					borderBottom: "15px solid #fff",
					zIndex: 9999,
					position: "absolute",
					top: 43,
					right: 30,
					transition: "2s",
					display: "none"
				}}
			></div> */}
			{renderNotifications}

			<Suspense fallback={<div />}>
				<LazyCustomDialog
					dialogProps={logoutDialogProps}
					handleClose={() =>
						setLogoutDialogProps(Object.assign({}, logoutDialogProps, { openDialog: false }))
					}
				/>
			</Suspense>
			<Suspense fallback={<div />}>
				<LazyCustomPdfViewerDialog
					dialogProps={openPdfViewDialogProps}
					handleClose={handlePdfViewDialogClose}
					pdfFile={dashboardHelpPDF}
				/>
			</Suspense>
			<CssBaseline />
			<AppBar
				position="fixed"
				className={classes.appBar}
				style={{
					// [theme.breakpoints.up("m")]: {
					width: `calc(100% - ${drawerWidth}px)`,
					marginLeft: drawerWidth,
					backgroundColor: "#EEEEEE",
					color: "black"

					// backgroundColor: "#EEEEEE",
					// color: "black"
				}}
			>
				<Toolbar className="toolbar">
					{!mobileOpen && (
						<IconButton
							color="primary"
							aria-label="open drawer"
							edge="start"
							onClick={handleDrawerToggle}
							className={classes.menuButton}
						>
							<MenuIcon />
						</IconButton>
					)}
					<div className="appbar-header" id="appbar-header">
						{dashboardHeader}
					</div>

					<div className={classes.grow} />
					<div className={classes.sectionDesktop + " sec-desk"}>
						{ADMIN_ENV === "DEV" ? (
							<div className="test-portal-container" id="test-portal-container">
								<Chip label="Test Portal" color="secondary" />
							</div>
						) : null}
						{/* <IconButton aria-label="show 4 new mails">
							<Badge badgeContent={4} color="secondary">
								<MailIcon style={{ color: "#4e4e4e" }} />
							</Badge>
						</IconButton> */}
						{/* <div className="select-group-name-container">
							<DropDown
								value={selectedGroupName}
								handleGroupNameChange={handleGroupNameChange}
								items={groupName}
							/>
						</div> */}

						{ADMIN_ENV === "DEV" || ADMIN_ENV === "LOCAL" ? (
							<Tooltip title="Support" enterDelay={500} leaveDelay={200}>
								<IconButton aria-label="help-icon" color="inherit" onClick={handlePdfViewDialogOpen}>
									<HelpOutlineIcon style={{ color: "#4e4e4e" }} />
								</IconButton>
							</Tooltip>
						) : null}
						<Tooltip title="Notifications" enterDelay={500} leaveDelay={200}>
							<IconButton aria-label="show new notifications" color="inherit">
								<Badge badgeContent={newNotifications ? notificationCount : ""} color="secondary">
									<NotificationsIcon style={{ color: "#4e4e4e" }} onClick={handlePopOverOpen} />
								</Badge>
							</IconButton>
						</Tooltip>
						<Tooltip title="Profile" enterDelay={500} leaveDelay={200}>
							<IconButton
								edge="end"
								aria-label="account of current user"
								aria-controls={menuId}
								aria-haspopup="true"
								onClick={handleProfileMenuOpen}
								color="inherit"
							>
								<AccountCircle style={{ color: "#4e4e4e" }} />
							</IconButton>
						</Tooltip>

						{/* <Modal
							open={openPdfModal}
							onClose={handlePdfModalClose}
							aria-labelledby="simple-modal-title"
							aria-describedby="simple-modal-description"
						>
							<object
								data="https://media.geeksforgeeks.org/wp-content/cdn-uploads/20210101201653/PDF.pdf"
								width="800"
								height="500"
							></object>
						</Modal> */}
					</div>
					<div className={classes.sectionMobile}>
						<IconButton
							aria-label="show more"
							aria-controls={mobileMenuId}
							aria-haspopup="true"
							onClick={handleMobileMenuOpen}
							color="inherit"
						>
							<MoreIcon />
						</IconButton>
					</div>
				</Toolbar>
			</AppBar>
			{renderMobileMenu}
			{/* {renderGroupNameSelectionMenu} */}
			{renderMenu}
			<nav
				className={classes.drawer}
				aria-label="mailbox folders"
				style={{
					[theme.breakpoints.up("md")]: {
						width: drawerWidth,
						flexShrink: 0
					}
				}}
			>
				{/* <Hidden smUp implementation="css"> */}

				<Drawer
					container={container}
					variant="persistent"
					open={mobileOpen}
					onClose={handleDrawerToggle}
					// classes={{
					// 	paper: classes.drawerPaper
					// }}
					ModalProps={{
						keepMounted: true // Better open performance on mobile.,
					}}
					style={{
						width: drawerWidth,
						backgroundColor: "#3A4652"
					}}
				>
					<div id="admin-dashboard" className="admin-dashboard">
						<div className={classes.toolbar} />

						{/* {mobileOpen && (
				<IconButton
					color="inherit"
					aria-label="open drawer"
					edge="start"
					onClick={handleDrawerToggle}

					// className={classes.menuButton}
				>
					<ChevronLeftIcon />
				</IconButton>
			)} */}

						<div className="welcome-container">
							<div className="sidebar-icon-container">
								{mobileOpen && (
									<IconButton
										color="inherit"
										aria-label="open drawer"
										edge="start"
										onClick={handleDrawerToggle}

										// style={{ fontSize: "2rem" }}
										// className={classes.menuButton}
									>
										<Avatar style={{ width: 24, height: 24, backgroundColor: "#558b2f" }}>
											<ChevronLeftIcon />
										</Avatar>
										{/* <ChevronLeftIcon style={{ fontSize: "2rem" }} /> */}
									</IconButton>
								)}
							</div>
							<div className="sidebar-logo-container">
								<img src={nexcaliberLogo} alt="" />
							</div>

							<div className="dashboard-type-text" id="dashboard-type-text">
								<div>
									{user?.role === ROLES.super_admin
										? initCapitalize(ROLES.super_admin)
										: initCapitalize(ROLES.admin)}
								</div>
								{/* <div className="sidebar-icon-container">
						{mobileOpen && (
							<IconButton
								color="inherit"
								aria-label="open drawer"
								edge="start"
								onClick={handleDrawerToggle}

								// style={{ fontSize: "2rem" }}
								// className={classes.menuButton}
							>
								<Avatar style={{ width: 24, height: 24 }}>
									<ChevronLeftIcon />
								</Avatar>
								{/* <ChevronLeftIcon style={{ fontSize: "2rem" }} /> */}
							</div>
							<div></div>

							<StyledBadge
								overlap="circular"
								anchorOrigin={{
									vertical: "bottom",
									horizontal: "right"
								}}
								variant="dot"
							>
								<Avatar alt="Remy Sharp" className={classes.avatarColor}>
									<div className="avatar-text">
										{user && user.first_name.charAt(0) + user.last_name.charAt(0)}
									</div>
								</Avatar>
							</StyledBadge>
							<div className="welcome-text" id="welcome-text" onClick={handleNameClick}>
								{user && user.first_name && user.last_name
									? user.first_name + " " + user.last_name
									: null}
							</div>
						</div>
						<Divider />
						<List>
							{user?.role &&
								sideBarOptions[user?.role === ROLES.super_admin ? "SUPER ADMIN" : "ADMIN"]?.map(
									(tab: Tab, tabIndex: number) => {
										return (
											<>
												{tab.subTabs.length > 0 ? (
													<Accordion className="list-item-accordion" key={tabIndex}>
														<AccordionSummary
															expandIcon={
																tab.subTabs.length > 0 ? <ExpandMoreIcon /> : <div />
															}
															aria-controls="panel1a-content"
															id="panel1a-header"
															className="list-item-accordion-summary"
														>
															<div style={{ marginRight: 10 }}>{tab.icon()}</div>
															<div>{tab.caption}</div>
														</AccordionSummary>
														<AccordionDetails>
															<List>
																{tab.subTabs.length !== 0 &&
																	tab.subTabs.map((subTab, subTabIndex) => {
																		const { caption, route } = subTab;
																		return (
																			<ListItem
																				button
																				key={subTabIndex}
																				className="list-item-container"
																				onClick={() => navigate(route)}
																				style={{
																					backgroundColor:
																						selectedTab.subTabIndex ===
																							subTabIndex &&
																						selectedTab.index === tabIndex
																							? "#85CE36"
																							: "inherit",
																					color:
																						selectedTab.subTabIndex ===
																							subTabIndex &&
																						selectedTab.index === tabIndex
																							? "#4e4e4e"
																							: "inherit",
																					fontWeight:
																						selectedTab.subTabIndex ===
																							subTabIndex &&
																						selectedTab.index === tabIndex
																							? "bolder"
																							: "inherit"
																				}}
																			>
																				<ListItemText
																					primary={caption}
																					className="list-item-text"
																					style={{
																						color:
																							selectedTab.subTabIndex ===
																								subTabIndex &&
																							selectedTab.index ===
																								tabIndex
																								? "#4e4e4e"
																								: "inherit",
																						fontWeight:
																							selectedTab.subTabIndex ===
																								subTabIndex &&
																							selectedTab.index ===
																								tabIndex
																								? "bolder"
																								: "inherit"
																					}}
																				/>
																			</ListItem>
																		);
																	})}
															</List>
														</AccordionDetails>
													</Accordion>
												) : tab.route === "/bulk-text" ? (
													// (
													// 	(user.first_name === "Chitrak" && user.last_name === "Biswas") ||
													// 	(user.first_name === "Anupam" && user.last_name === "Biswas") ||
													// 	(user.first_name === "Arpita" && user.last_name === "Chowdhury") ||
													// 	(user.first_name === "Debashis" && user.last_name === "Dolui") ||
													// 	(user.first_name === "Abir" && user.last_name === "Samanta") ||
													// 	(user.first_name === "Suman" && user.last_name === "Dutta") ||
													// 	(user.first_name === "Babulal" && user.last_name === "Mahto") ||
													// 	(user.first_name === "Shivpujan" && user.last_name === "Kumar") ||
													// 	(user.first_name === "Saurabh" && user.last_name === "Bansode") ?
													<Accordion
														className="list-item-accordion"
														key={tabIndex}
														expanded={false}
														onClick={() => navigate(tab.route)}
													>
														<AccordionSummary
															expandIcon={
																tab.subTabs.length > 0 ? <ExpandMoreIcon /> : <div />
															}
															aria-controls="panel1a-content"
															id="panel1a-header"
															className="list-item-accordion-summary"
														>
															<div style={{ marginRight: 10 }}>{tab.icon()}</div>
															<div>{tab.caption}</div>
														</AccordionSummary>
													</Accordion>
												) : // 	: null
												// )
												tab.route === "/veb-data-export" ? (
													// (
													// 	user.first_name === "Chitrak" && user.last_name === "Biswas" ? (
													<Accordion
														className="list-item-accordion"
														key={tabIndex}
														expanded={false}
														onClick={() => navigate(tab.route)}
													>
														<AccordionSummary
															expandIcon={
																tab.subTabs.length > 0 ? <ExpandMoreIcon /> : <div />
															}
															aria-controls="panel1a-content"
															id="panel1a-header"
															className="list-item-accordion-summary"
														>
															<div style={{ marginRight: 10 }}>{tab.icon()}</div>
															<div>{tab.caption}</div>
														</AccordionSummary>
													</Accordion>
												) : (
													// )
													// 	: null
													// )
													<Accordion
														className="list-item-accordion"
														key={tabIndex}
														expanded={false}
														onClick={() => navigate(tab.route)}
													>
														<AccordionSummary
															expandIcon={
																tab.subTabs.length > 0 ? <ExpandMoreIcon /> : <div />
															}
															aria-controls="panel1a-content"
															id="panel1a-header"
															className="list-item-accordion-summary"
														>
															<div style={{ marginRight: 10 }}>{tab.icon()}</div>
															<div>{tab.caption}</div>
														</AccordionSummary>
													</Accordion>
												)}
											</>
										);
									}
								)}
						</List>
					</div>
				</Drawer>

				{/* <div>
					<Drawer
						classes={{
							paper: classes.drawerPaper
						}}
						variant="permanent"
						open
					>
						{drawer}
					</Drawer>
				</div> */}
			</nav>
			<main className={classes.content + " main"}>
				<div className={classes.toolbar} />
				<Suspense fallback={<div />}>
					{/* <WrappedComponent /> */}
					<Outlet />
				</Suspense>
			</main>
		</div>
	);
};

export default Sidebar;
